import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Snackbar,
    Alert,
    CircularProgress,
    Divider,
    Chip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    ContentCopy as CopyIcon,
    Share as ShareIcon,
    EmojiEvents as TrophyIcon,
    PersonAdd as PersonAddIcon,
    Link as LinkIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { trackFlexEvent, FLEX_EVENTS } from '../../utils/flexAnalytics';

export default function ChallengeFriendDialog({ open, onClose }) {
    const [inviteMethod, setInviteMethod] = useState('email');
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleReset = () => {
        setEmail('');
        setUsername('');
        setError('');
        setResult(null);
        setCopied(false);
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleCreate = async () => {
        setError('');

        if (inviteMethod === 'email') {
            if (!email.trim()) {
                setError('Please enter an email address.');
                return;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) {
                setError('Please enter a valid email address.');
                return;
            }
        } else {
            if (!username.trim()) {
                setError('Please enter a username.');
                return;
            }
        }

        setCreating(true);
        try {
            const payload = {};
            if (inviteMethod === 'email') {
                payload.challengedEmail = email.trim();
            } else {
                payload.challengedUsername = username.trim();
            }

            const res = await api.post('/challenges/create', payload);
            setResult(res.data);
            trackFlexEvent(FLEX_EVENTS.CHALLENGE_CREATED, {
                method: inviteMethod,
                passage_id: res.data.passage?.id,
            });
            trackFlexEvent(FLEX_EVENTS.CHALLENGE_INVITE_SENT, {
                method: inviteMethod,
            });
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to create challenge.';
            setError(msg);
        } finally {
            setCreating(false);
        }
    };

    const handleCopyLink = () => {
        if (result?.shareUrl) {
            navigator.clipboard.writeText(result.shareUrl);
            setCopied(true);
            setSnackbar({ open: true, message: 'Challenge link copied!', severity: 'success' });
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleShare = async () => {
        if (!result?.shareUrl) return;
        const shareData = {
            title: 'ReaderFLEX Challenge',
            text: `I challenge you to a reading speed duel on ReaderFLEX! Think you can read faster and understand more than me? Prove it!`,
            url: result.shareUrl,
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                handleCopyLink();
            }
        } catch (err) {
            if (err.name !== 'AbortError') {
                handleCopyLink();
            }
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrophyIcon color="primary" />
                        <Typography variant="h6">Challenge a Friend</Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    {result ? (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <TrophyIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h6" gutterBottom>
                                Challenge Created!
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Send this link to your friend to start the head-to-head reading challenge.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <TextField
                                    value={result.shareUrl}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                />
                                <IconButton
                                    onClick={handleCopyLink}
                                    color={copied ? 'success' : 'primary'}
                                    size="small"
                                >
                                    <CopyIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <Chip
                                    label={`${result.passage?.title || 'Random passage'}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    label={`${result.passage?.wordCount || ''} words`}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Challenge a friend to a head-to-head reading duel. You'll both read the same passage and answer comprehension questions. Fastest reader with the best comprehension wins!
                            </Typography>

                            <ToggleButtonGroup
                                value={inviteMethod}
                                exclusive
                                onChange={(e, val) => val && setInviteMethod(val)}
                                fullWidth
                                sx={{ mb: 2 }}
                            >
                                <ToggleButton value="email">
                                    <PersonAddIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                    By Email
                                </ToggleButton>
                                <ToggleButton value="username">
                                    <PersonAddIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                    By Username
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {inviteMethod === 'email' ? (
                                <TextField
                                    label="Friend's email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    fullWidth
                                    size="small"
                                    placeholder="friend@example.com"
                                    disabled={creating}
                                    error={!!error && inviteMethod === 'email'}
                                    helperText={error && inviteMethod === 'email' ? error : ''}
                                />
                            ) : (
                                <TextField
                                    label="Friend's username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    fullWidth
                                    size="small"
                                    placeholder="username"
                                    disabled={creating}
                                    error={!!error && inviteMethod === 'username'}
                                    helperText={error && inviteMethod === 'username' ? error : ''}
                                />
                            )}

                            {error && !email && !username && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {error}
                                </Alert>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip label="5 comprehension questions" size="small" variant="outlined" />
                                <Chip label="Effective WPM scoring" size="small" variant="outlined" />
                                <Chip label="48-hour window" size="small" variant="outlined" />
                            </Box>
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {result ? (
                        <>
                            <Button onClick={handleReset} color="inherit">
                                New Challenge
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<ShareIcon />}
                                onClick={handleShare}
                            >
                                Share Link
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={handleClose} color="inherit">
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={creating ? <CircularProgress size={18} /> : <SendIcon />}
                                onClick={handleCreate}
                                disabled={creating}
                            >
                                {creating ? 'Creating...' : 'Send Challenge'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
